import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import Input from "../../../components/Input/Input"
import { getBillingInfo, getBillingSecrets } from "../../../features/bankAccountLink/bankAccountLink"
import { formatCreditCardNumber, formatCVC, formatExpirationDate } from "../../../utils/payment"
import Dropdown from "../../../components/Dropdown/Dropdown"
import { LuEye, LuPencil } from "react-icons/lu"


const CreditCardContainer = ({ billingId, setBillingAddress, viewOnly = false, setCardUpdateData, setCardSecretsUpdateData }) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const [reqError, setReqError] = useState('')
  const [secretsLoaded, setSecretsLoaded] = useState(false)
  const [editingSecrets, setEditingSecrets] = useState(false)

  const [creditCard, setCreditCard] = useState({
    provider: '',
    nameOnCard: '',
    cardNumberLast4: '',
    billingAddress: {
      address: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
    },
    expiry: '',
  })

  const [creditCardSecrets, setCreditCardSecrets] = useState({
    cvv: '',
    cardNumber: '',
  })

  useEffect(() => {
    if (reqError) {
      showSnackBar("error", reqError)
      setReqError('')
    }
  }, [reqError])

  const getCreditCardDetails = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getBillingInfo({ billingAccountId: billingId, billingType: 'card' }))
      const {
        provider,
        nameOnCard,
        cardNumberLast4,
        billingAddress,
        expiryMonth,
        expiryDate
      } = response?.payload?.data

      const expiry = formatExpirationDate(`${expiryMonth}${expiryDate}`)
      setCreditCard({
        provider: provider || '',
        nameOnCard: nameOnCard || '',
        cardNumberLast4: cardNumberLast4 || '',
        billingAddress: {
          address: billingAddress?.address || '',
          address2: billingAddress?.address2 || '',
          city: billingAddress?.city || '',
          state: billingAddress?.state || '',
          zipcode: billingAddress?.zipcode || '',
        },
        expiry: expiry || '',
      })
      setBillingAddress(billingAddress)
      setCardUpdateData({
        provider,
        nameOnCard,
        expiryDate,
        expiryMonth,
      })
    } catch (error) {
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }

  const getCreditCardSecrets = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getBillingSecrets({ billingAccountId: billingId }))
      const { cvv, cardNumber } = response?.payload?.data
      setCreditCardSecrets({
        cvv: cvv || '',
        cardNumber: cardNumber || '',
      })
      setSecretsLoaded(true)
    } catch (error) {
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }

  const handleInputChange = ({ target }) => {
    if (viewOnly) return
    let value = target.value

    if (target.name === "expiry") {
      value = formatExpirationDate(value)
      setCardUpdateData((prev) => ({
        ...prev,
        expiryMonth: value.substring(0, 2),
        expiryDate: value.substring(3, 5),
      }))
    } else if (target.name === "provider") {
      const reformattedCardNumber = formatCreditCardNumber(creditCardSecrets.cardNumber, value)
      const reformattedCvv = formatCVC(creditCardSecrets.cvv, { number: reformattedCardNumber, provider: value })
      setCreditCardSecrets({ cardNumber: reformattedCardNumber, cvv: reformattedCvv })
      setCardSecretsUpdateData((prev) => ({ ...prev, cardNumber: reformattedCardNumber, cvv: reformattedCvv }))
    }

    setCreditCard((prev) => ({
      ...prev,
      [target.name]: value,
    }))
    setCardUpdateData((prev) => ({ ...prev, [target.name]: value }))
  }

  const handleSecretsChange = ({ name, value }) => {
    if (viewOnly) return
    if (name === "cardNumber") {
      value = formatCreditCardNumber(value, creditCard.provider)
    } else if (name === "cvv") {
      value = formatCVC(value, { number: creditCardSecrets.cardNumber, provider: creditCard.provider })
    }
    setCreditCardSecrets((prev) => ({ ...prev, [name]: value }))
    setCardSecretsUpdateData((prev) => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    getCreditCardDetails()
  }, [])

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider">
      {/* Provider */}
      {viewOnly ? (
        <Input width="w-full" placeholder="Provider" label="Provider" disabled value={creditCard.provider} />
      ) : (
        <Dropdown
          menuItems={[
            { label: 'Visa', value: 'Visa' },
            { label: 'Mastercard', value: 'Mastercard' },
            { label: 'American Express', value: 'American Express' },
            { label: 'Discover', value: 'Discover' },
          ]}
          label="Provider"
          placeholder="Provider"
          value={creditCard.provider}
          setValue={(value) => handleInputChange({ target: { name: 'provider', value } })}
        />
      )}

      {/* Card Number */}
      <Input
        width="w-full"
        placeholder="Card Number"
        label="Card Number"
        name="cardNumber"
        disabled={!editingSecrets}
        value={
          editingSecrets
            ? creditCardSecrets.cardNumber
            : secretsLoaded
              ? creditCardSecrets.cardNumber
              : `**** **** **** ${creditCard.cardNumberLast4}`
        }
        onChange={(e) => handleSecretsChange({ name: 'cardNumber', value: e.target.value })}
        rightSideIconStyle="mt-4 mr-4 flex gap-2"
        rightSideIcon={!viewOnly && (
          <>
            {!secretsLoaded && (
              <div className="cursor-pointer" onClick={async () => {
                await getCreditCardSecrets()
              }}>
                <LuEye size={18} className="text-text-secondary" />
              </div>
            )}

            {secretsLoaded && !editingSecrets && (
              <div className="cursor-pointer" onClick={() => {
                setEditingSecrets(true)
                setCreditCardSecrets(prev => ({ ...prev, cvv: '' }))
              }}>
                <LuPencil size={18} className="text-text-secondary" />
              </div>
            )}
          </>
        )}
      />

      {/* Name on Card */}
      <Input
        width="w-full"
        placeholder="Name on Card"
        label="Name on Card"
        name="nameOnCard"
        onChange={handleInputChange}
        value={creditCard.nameOnCard}
        disabled={viewOnly}
      />

      {/* Expiry */}
      <Input
        label="Expire Date"
        value={creditCard.expiry}
        type="tel"
        name="expiry"
        placeholder="MM/YY"
        required
        onChange={handleInputChange}
        disabled={viewOnly}
      />

      {/* CVV */}
      <Input
        label="CVV"
        value={
          editingSecrets
            ? creditCardSecrets.cvv
            : secretsLoaded
              ? creditCardSecrets.cvv
              : ''
        }
        type="text"
        placeholder="***"
        disabled={!editingSecrets}
        onChange={(e) => handleSecretsChange({ name: 'cvv', value: e.target.value })}
        rightSideIconStyle="mt-4 mr-4"
        rightSideIcon={!viewOnly && (
          !secretsLoaded ? (
            <div className="cursor-pointer" onClick={async () => {
              await getCreditCardSecrets()
              setSecretsLoaded(true)
            }}>
              <LuEye size={18} className="text-text-secondary" />
            </div>
          ) : !editingSecrets ? (
            <div className="cursor-pointer" onClick={() => {
              setEditingSecrets(true)
              setCreditCardSecrets(prev => ({ ...prev, cvv: '' }))
            }}>
              <LuPencil size={18} className="text-text-secondary" />
            </div>
          ) : null
        )}
      />
    </div>
  )
}

export default CreditCardContainer
